import React, { useState, useEffect, useMemo, memo } from "react";
import styles from "./depressionTest.module.css";

import { handle_tools_services } from "../../utils/services";

const CircularProgress = memo(({ 
  percentage, 
  score, 
  animateProgress, 
  interpretationTitle, 
  interpretationColorClass 
}) => {
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  
  // Calculate offset based on percentage and animation state
  const offset = useMemo(() => {
    return animateProgress
      ? circumference - (percentage / 100) * circumference
      : circumference;
  }, [percentage, animateProgress, circumference]);

  // Get color class based on score - moved to useMemo
  const colorClass = useMemo(() => {
    if (score >= 0 && score <= 4) return styles.excellentColor;
    if (score >= 5 && score <= 9) return styles.goodColor;
    if (score >= 10 && score <= 14) return styles.moderateColor;
    if (score >= 15 && score <= 19) return styles.needsAttentionColor;
    if (score >= 20 && score <= 27) return styles.severeColor;
    return styles.neutralColor; // Fallback for invalid scores
  }, [score]);

  return (
    <div className={styles.circularProgress}>
      {/* Background circle */}
      <svg className={styles.progressSvg} viewBox="0 0 180 180">
        <circle
          cx="90"
          cy="90"
          r={radius}
          className={styles.progressBackground}
        />
        {/* Progress circle */}
        <circle
          cx="90"
          cy="90"
          r={radius}
          className={`${styles.progressCircle} ${colorClass} ${
            animateProgress ? styles.animate : ""
          }`}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 90 90)"
        />
      </svg>
      {/* Score text */}
      <div className={styles.progressText}>
        <span className={`${styles.percentage} ${colorClass}`}>
          {animateProgress ? score : 0}
        </span>
        <span className={`${styles.interpretationTitle} ${interpretationColorClass}`}>
          {interpretationTitle}
        </span>
      </div>
    </div>
  );
});

const GeneralHealthTest = () => {

  const quizData = [
    {
      question: "Little interest or pleasure in doing things.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 1,
    },
    {
      question: "Feeling down, depressed, or hopeless.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 2,
    },
    {
      question: "Trouble falling or staying asleep, or sleeping too much.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 3,
    },
    {
      question: "Feeling tired or having little energy.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 4,
    },
    {
      question:
        "Feeling bad about yourself – or that you are a failure or have let yourself or your family down.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 5,
    },
    {
      question:
        "Trouble concentrating on things, such as reading the newspaper or watching television.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 6,
    },
    {
      question:
        "Moving or speaking so slowly that other people could have noticed. Or the opposite – being so fidgety or restless that you have been moving around a lot more than usual.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 7,
    },
    {
      question:
        "Thoughts that you would be better off dead, or of hurting yourself in some way.",
      options: [
        { text: "Not at all", score: 0 },
        { text: "Several days", score: 1 },
        { text: "More than half the days", score: 2 },
        { text: "Nearly everyday", score: 3 },
      ],
      answer: null,
      index: 8,
    },
  ];

  // State management
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [answerValues, setAnswerValues] = useState(
    Array(quizData.length).fill(null)
  );
  const [showResult, setShowResult] = useState(false);
  const [name, setName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [animateProgress, setAnimateProgress] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Calculate total possible score
  const maxPossibleScore = 24; // 8 questions with 3 points

  // Calculate user's score - memoized to prevent unnecessary recalculations
  const calculatedScore = useMemo(() => {
    return answerValues.reduce((sum, value) => sum + (value || 0), 0);
  }, [answerValues]);

  // Calculate score percentage - memoized
  const scorePercentage = useMemo(() => {
    return (calculatedScore / maxPossibleScore) * 100;
  }, [calculatedScore, maxPossibleScore]);

  useEffect(() => {
    if (showResult) {
      // Start animation after results are shown
      setTimeout(() => {
        setAnimateProgress(true);
      }, 300);
    }
  }, [showResult]);

  // Get interpretation based on score - memoized
  const interpretation = useMemo(() => {
    if (calculatedScore >= 0 && calculatedScore <= 4) {
      return {
        title: "Excellent Health",
        description:
          "Your results indicate excellent overall health with minimal concerns.",
        colorClass: styles.excellent,
      };
    } else if (calculatedScore >= 5 && calculatedScore <= 9) {
      return {
        title: "Good Health",
        description:
          "Your health appears good. Consider reviewing your lifestyle habits at your next check-up.",
        colorClass: styles.good,
      };
    } else if (calculatedScore >= 10 && calculatedScore <= 14) {
      return {
        title: "Moderate Health Concerns",
        description:
          "Some health indicators suggest areas for improvement. Consider discussing these with a healthcare provider.",
        colorClass: styles.moderate,
      };
    } else if (calculatedScore >= 15 && calculatedScore <= 19) {
      return {
        title: "Significant Health Concerns",
        description:
          "Several health indicators suggest the need for medical attention. Consultation with a healthcare provider is recommended.",
        colorClass: styles.needsAttention,
      };
    } else if (calculatedScore >= 20 && calculatedScore <= 27) {
      return {
        title: "Serious Health Concerns",
        description:
          "Your results indicate potentially serious health issues that should be addressed promptly. Immediate consultation with a healthcare provider is strongly recommended.",
        colorClass: styles.severe,
      };
    } else {
      return {
        title: "Invalid Score",
        description:
          "Unable to calculate a valid score. Please ensure all questions are answered.",
        colorClass: styles.neutral,
      };
    }
  }, [calculatedScore]);

  // Handler for selecting an answer
  const handleAnswerSelect = (option, index) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = option.text;
    setAnswers(newAnswers);

    const newValues = [...answerValues];
    newValues[currentQuestion] = option.score;
    setAnswerValues(newValues);

    // Set transitioning state to trigger animation
    setTransitioning(true);

    // Auto advance to next question after a short delay
    setTimeout(() => {
      if (currentQuestion < quizData.length - 2) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setShowResult(true);
      }
      // Reset transitioning state after moving to next question
      setTransitioning(false);
    }, 500); // 500ms delay before changing question
  };

  // Navigation handlers
  const handleNextQuestion = () => {
    if (currentQuestion < quizData.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Reset quiz
  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers(Array(quizData.length).fill(null));
    setAnswerValues(Array(quizData.length).fill(null));
    setShowResult(false);
    setFormSubmitted(false);
    setAnimateProgress(false);
    setSubmitError(null);
  };

  // Prepare the complete assessment data
  const prepareAssessmentData = () => {
    const interpretation = getInterpretation().title;

    // Collect detailed responses in the format expected by the details view
    const detailedResponses = quizData.map((question, index) => ({
      question: question.question,
      answer: answers[index] || "No response",
    }));

    // Create the data object in the structure expected by the panel components
    return {
      fullName: name,
      contactNumber: contactNumber,
      email: email,
      submissionDate: new Date().toISOString(),
      score: calculatedScore,
      responses: detailedResponses,
      comments: "", // Adding this field to match the expected structure in details view
      // Include the detailed data to maintain all the information
      detailedAssessment: {
        totalScore: calculatedScore,
        maxPossibleScore: maxPossibleScore,
        interpretationCategory: interpretation,
        detailedResponses: quizData.map((question, index) => ({
          questionIndex: question.index,
          questionText: question.question,
          answerText: answers[index],
          answerScore: answerValues[index] || 0,
        })),
      },
    };
  };

  // Handle email submission and data saving
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Collect all assessment data
      const assessmentData = prepareAssessmentData();
     
      // Send data to database using the postData utility
      const response = await handle_tools_services(assessmentData, "health");
      setFormSubmitted(true);
    } catch (error) {
      console.error("Error submitting assessment data:", error);
      setSubmitError(
        "There was an error submitting your data. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to determine option style based on selection
  const getOptionClass = (option) => {
    const isSelected = answers[currentQuestion] === option.text;

    if (transitioning && isSelected) {
      return `${styles.option} ${styles.selected} ${styles.transitioning}`;
    }

    return isSelected ? `${styles.option} ${styles.selected}` : styles.option;
  };

  // Helper function to get interpretation - used to avoid memoized function recreation
  const getInterpretation = () => interpretation;

  return (
    <>
      <div className={styles.assessmentBanner}>
        <div
          className={[styles.bannerDecoration, styles.decoration1].join(" ")}
        ></div>
        <div
          className={[styles.bannerDecoration, styles.decoration2].join(" ")}
        ></div>

        <div className={styles.bannerContent}>
          <div className={styles.bannerHeader}>
            <h1 className={styles.SelfBannerTitle}>
              General Health Self Assessment
            </h1>
            <p className={styles.SelfBannerTitle}>
              A confidential screening tool to help evaluate your mental
              wellbeing
            </p>
          </div>

          <div className={styles.bannerInfo}>
            <p className={styles.bannerDisclaimer}>
              This assessment is based on the General Health test. It is a
              diagnostic tool, help identify symptoms that may warrant
              professional attention.
            </p>
          </div>
        </div>
      </div>

      <div className={styles.wellnessQuiz}>
        {!showResult ? (
          <div className={styles.quizContainer}>
            <div className="decorative-circles-MN">
              <div className="circle-1-MN"></div>
              <div className="circle-2-MN"></div>
            </div>

            <div className={styles.quizHeader}>
              <p className={styles.questionCounter}>
                Question {currentQuestion + 1} of {quizData.length}
              </p>
              <div className={styles.progressBarContainer}>
                <div
                  className={styles.progressBar}
                  style={{
                    width: `${
                      ((currentQuestion + 1) / quizData.length) * 100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div
              className={`${styles.questionContainer} ${
                transitioning ? styles.fadeOut : styles.fadeIn
              }`}
            >
              <h2 className={styles.questionText}>
                {quizData[currentQuestion].question}
              </h2>

              <div className={styles.optionsContainer}>
                {quizData[currentQuestion].options.map((option, index) => (
                  <div
                    key={index}
                    className={getOptionClass(option)}
                    onClick={() => handleAnswerSelect(option, index)}
                  >
                    <span>{option.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.navigationButtons}>
              <button
                onClick={handlePreviousQuestion}
                disabled={currentQuestion === 0}
                className={
                  currentQuestion === 0
                    ? `${styles.buttonPrevious} ${styles.disabled}`
                    : styles.buttonPrevious
                }
              >
                Previous
              </button>

              {currentQuestion === quizData.length - 1 ? (
                <button
                  onClick={handleNextQuestion}
                  className={styles.buttonNext}
                >
                  Complete
                </button>
              ) : (
                <button
                  onClick={handleNextQuestion}
                  className={styles.buttonPrevious}
                >
                  Skip
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.resultsContainer}>
            <h2 className={styles.resultsTitle}>Assessment Complete</h2>
            <p className={styles.resultsSubtitle}>
             Thank you for completing this General Health screening.
            </p>

            <div className={styles.resultsContent}>
              {/* Left side: Circular progress */}
              <div className={styles.resultsProgress}>
                <CircularProgress 
                  percentage={scorePercentage} 
                  score={calculatedScore}
                  animateProgress={animateProgress}
                  interpretationTitle={interpretation.title}
                  interpretationColorClass={interpretation.colorClass}
                />
              </div>

              {/* Right side: Score interpretation - with relative position for overlay */}
              <div className={styles.interpretationWrapper}>
                {/* The interpretation content will always be blurred */}
                <div className={`${styles.resultsInterpretation} ${styles.blurredContent}`}>
                  <h3
                    className={`${styles.interpretationTitle} ${
                      interpretation.colorClass
                    }`}
                  >
                    {interpretation.title}
                  </h3>
                  <p className={styles.interpretationDescription}>
                    {interpretation.description}
                  </p>
                  <div className={styles.scoreDetails}>
                    <p>
                      Score: {calculatedScore} out of {maxPossibleScore} points
                    </p>
                  </div>
                  <div className={styles.disclaimer}>
                    <p>
                    This is a clinical health assessment tool. However, results
                    should be reviewed with a qualified healthcare professional
                    who can provide proper context, and interpretation advice
                    based on your complete health history and current condition
                    </p>
                  </div>
                </div>

                {/* Overlay content - either form or thank you message */}
                <div className={styles.interpretationOverlay}>
                  {formSubmitted ? (
                    <div className={styles.formContainer}>
                      <div className={styles.thankyouMN}>
                        <div className={styles.checkiconMN}>✓</div>
                        <h2 className={styles.formtitleMN}>Thank You!</h2>
                        <p className="form-subtitle-MN">
                        Your Mental wellness is precious to us.
                        </p>
                        <p className="form-subtitle-MN">
                          Your Detailed Report Has Been Successfully Sent To Your
                          Provided Email.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.formContainer}>
                      <h3 className={styles.formTitle}>Get Your Full Report</h3>
                      <p className={styles.formSubtitle}>
                        Enter your details to receive a personalized assessment
                        report with detailed insights and recommendations.
                      </p>

                      {submitError && (
                        <p className={styles.errorMessage}>{submitError}</p>
                      )}

                      <form
                        onSubmit={handleSubmit}
                        className={styles.emailForm}
                      >
                        <div className={styles.formField}>
                          <label htmlFor="name" className={styles.formLabel}>
                            Name
                          </label>
                          <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className={styles.formInput}
                            placeholder="Your name"
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className={styles.formField}>
                          <label
                            htmlFor="contactNumber"
                            className={styles.formLabel}
                          >
                            Contact Number
                          </label>
                        <input
  type="tel"
  id="contactNumber"
  inputMode="numeric"
  pattern="[0-9]*"
  value={contactNumber}
  onChange={(e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setContactNumber(value);
    }
  }}
  required
  className={styles.formInput}
  placeholder="Your contact number"
  disabled={isSubmitting}
/>
                        </div>
                        <div className={styles.formField}>
                          <label htmlFor="email" className={styles.formLabel}>
                            Email
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className={styles.formInput}
                            placeholder="Your email address"
                            disabled={isSubmitting}
                          />
                        </div>

                        <button
                          type="submit"
                          className={styles.formButton}
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? "Processing..."
                            : "Get My Detailed Report"}
                        </button>

                        <p className={styles.privacyNote}>
                          We respect your privacy and will never share your
                          information with third parties.
                        </p>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button onClick={resetQuiz} className={styles.resetButton}>
              Take Assessment Again
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default GeneralHealthTest;